import env from 'config/env';
import devTF from 'config/envByHost/dev-tf';
import { ACT_AS_TEACHER_KEY } from 'config/localStorageKeys';

// DEV only (localhost and the deployed DEV app): with a teacher id in localStorage[ACT_AS_TEACHER_KEY], every
// call runs as that teacher on a token minted by POST /v1/token/exchange. The signed-in user needs the
// ImpersonateUser permission.
const EXPIRY_BUFFER_MS = 60_000;

interface Minted {
  token: string;
  expiresAt: number;
}

let cache: { teacherId: string; minted: Promise<Minted> } | null = null;

const isDevApi = () => env.TF_SVCGATEWAY_URL === devTF.TF_SVCGATEWAY_URL;

const actAsTeacherId = (): string | null => {
  try {
    return window.localStorage.getItem(ACT_AS_TEACHER_KEY)?.trim() || null;
  } catch {
    return null;
  }
};

const mint = async (ownToken: string, teacherId: string): Promise<Minted> => {
  const resp = await fetch(`${env.TF_SVCGATEWAY_URL}/v1/token/exchange`, {
    method: 'POST',
    headers: { authorization: `Bearer ${ownToken}`, 'content-type': 'application/json' },
    body: JSON.stringify({ audience: ['tf-api'], subject: teacherId, subject_type: 'teacher_id' }),
  });
  if (!resp.ok) {
    throw new Error(`act-as: token exchange for teacher ${teacherId} failed with ${resp.status}`);
  }
  const { access_token: token, expires_in: expiresIn }: { access_token: string; expires_in: number } =
    await resp.json();
  return { token, expiresAt: Date.now() + expiresIn * 1000 };
};

export const actAsToken = async (ownToken: string): Promise<string> => {
  const teacherId = isDevApi() ? actAsTeacherId() : null;
  if (!teacherId) {
    return ownToken;
  }
  const seen = cache?.teacherId === teacherId ? cache.minted : null;
  if (seen) {
    const minted = await seen.catch(() => null);
    if (minted && minted.expiresAt - EXPIRY_BUFFER_MS > Date.now()) {
      return minted.token;
    }
  }
  // Another caller may have started the refresh while this one waited; share it.
  if (cache?.teacherId !== teacherId || cache.minted === seen) {
    cache = { teacherId, minted: mint(ownToken, teacherId) };
  }
  return (await cache.minted).token;
};
